import type { NormalizedSwapQuote } from "../types"

export type SwapRouteDecisionReason =
  | "scale-route-healthy"
  | "scale-route-missing"
  | "scale-route-slow"
  | "scale-price-impact-unknown"
  | "scale-price-impact-too-high"
  | "external-meaningfully-better"
  | "external-fallback"
  | "no-route"

export type SwapRouteDecision = {
  quote: NormalizedSwapQuote | undefined
  reason: SwapRouteDecisionReason
  rejectedScaleQuote?: NormalizedSwapQuote
}

export type SwapRoutingPolicyConfig = {
  maxInternalQuoteMs: number
  maxInternalPriceImpactBps: number
  externalOutperformanceToleranceBps: number
}

const DEFAULT_POLICY: SwapRoutingPolicyConfig = {
  maxInternalQuoteMs: 1200,
  maxInternalPriceImpactBps: 1_500,
  externalOutperformanceToleranceBps: 75,
}

export class SwapRoutingPolicy {
  constructor(private readonly config: SwapRoutingPolicyConfig = DEFAULT_POLICY) {}

  chooseRoute(quotes: NormalizedSwapQuote[]): SwapRouteDecision {
    const scaleQuote = quotes.find((quote) => quote.aggregatorId === "scale")
    const fallbackQuote = this.getBestExternalQuote(quotes)

    if (!scaleQuote) {
      return fallbackQuote
        ? { quote: fallbackQuote, reason: "external-fallback" }
        : { quote: undefined, reason: "scale-route-missing" }
    }

    const scaleHealthReason = this.getScaleRejectionReason(scaleQuote, fallbackQuote)
    if (!scaleHealthReason) {
      return { quote: scaleQuote, reason: "scale-route-healthy" }
    }

    if (fallbackQuote) {
      return {
        quote: fallbackQuote,
        reason: scaleHealthReason === "external-meaningfully-better" ? "external-meaningfully-better" : "external-fallback",
        rejectedScaleQuote: scaleQuote,
      }
    }

    return {
      quote: undefined,
      reason: scaleHealthReason,
      rejectedScaleQuote: scaleQuote,
    }
  }

  private getScaleRejectionReason(
    scaleQuote: NormalizedSwapQuote,
    fallbackQuote: NormalizedSwapQuote | undefined,
  ): SwapRouteDecisionReason | null {
    const elapsedMs = Number(scaleQuote.transactionMetadata.routeElapsedMs ?? 0)
    if (elapsedMs > this.config.maxInternalQuoteMs) {
      return "scale-route-slow"
    }

    if (scaleQuote.priceImpactBps === null) {
      return "scale-price-impact-unknown"
    }

    if (scaleQuote.priceImpactBps > this.config.maxInternalPriceImpactBps) {
      return "scale-price-impact-too-high"
    }

    if (fallbackQuote && this.isExternalMeaningfullyBetter(scaleQuote, fallbackQuote)) {
      return "external-meaningfully-better"
    }

    return null
  }

  private isExternalMeaningfullyBetter(scaleQuote: NormalizedSwapQuote, fallbackQuote: NormalizedSwapQuote): boolean {
    if (scaleQuote.outputAmountRaw <= 0n) {
      return true
    }

    const improvementBps =
      Number(((fallbackQuote.outputAmountRaw - scaleQuote.outputAmountRaw) * 10_000n) / scaleQuote.outputAmountRaw)

    return improvementBps > this.config.externalOutperformanceToleranceBps
  }

  private getBestExternalQuote(quotes: NormalizedSwapQuote[]): NormalizedSwapQuote | undefined {
    return quotes
      .filter((quote) => quote.aggregatorId !== "scale")
      .reduce<NormalizedSwapQuote | undefined>((bestQuote, quote) => {
        if (!bestQuote || quote.outputAmountRaw > bestQuote.outputAmountRaw) {
          return quote
        }
        return bestQuote
      }, undefined)
  }
}
